// /privacy — the fine print, set like everything else here: one printed sheet.
// Written to be actually read: what we keep, what never leaves your browser,
// who else touches the data, and how to make us delete all of it.
// Linked from the site footer; also the privacy-policy URL on OAuth consent
// screens (Google requires a real one for production publishing).

import type { ReactNode } from "react";
import { Page } from "../components/Layout.tsx";
import {
  Sheet,
  Masthead,
  Tear,
  Field,
  Kicker,
  H3,
  Sub,
  Barcode,
} from "../components/sheet.tsx";

const EFFECTIVE = "July 8, 2026";
const CONTACT = "acachar2@gmail.com";

function Clause({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-5 first:mt-0">
      <h3 className="font-mono text-[11px] font-bold uppercase tracking-stamp text-ink-900">
        {title}
      </h3>
      <div className="mt-1.5 space-y-2 text-sm leading-relaxed text-ink-500">
        {children}
      </div>
    </div>
  );
}

export default function Privacy() {
  return (
    <Page testid="screen-privacy" wide bare>
      <Sheet>
        <Masthead right="The fine print" sub={`Effective ${EFFECTIVE}`} />

        <Field>
          <Kicker>Privacy policy</Kicker>
          <H3>Short version: your film taste, nothing else.</H3>
          <Sub>
            twoshot exists to compare two movie libraries. We keep the minimum
            needed to do that, we don't run ads, and we don't sell or share your
            data with data brokers. Ever.
          </Sub>
        </Field>

        <Tear />

        <Field>
          <Clause title="What we keep from your Letterboxd export">
            <p>
              Your export is distilled <em>in your browser</em> before anything
              is uploaded. We keep only: film titles, release years, your star
              ratings, watched/watchlist flags, and monthly counts of how many
              films you logged (for the activity chart).
            </p>
            <p>
              Your reviews, comments, likes, lists, and the raw export files
              never leave your device — we don't upload them, so we can't read
              them.
            </p>
          </Clause>

          <Clause title="Keeping your profile up to date">
            <p>
              Your upload is the baseline. After that, when someone opens your
              page, we read your{" "}
              <strong className="text-ink-900">public</strong> Letterboxd diary
              feed (the same RSS feed anyone can open at
              letterboxd.com/your-username/rss) and add films you've logged
              since — titles, years, ratings, and watch dates, the same fields
              as the export. It runs at most a few times a day and never reads
              anything private: if your Letterboxd profile is private, we simply
              get nothing.
            </p>
            <p>
              Your page always shows how many films came from this top-up, so
              you can tell the uploaded snapshot from the live additions. To
              stop it entirely, remove your Letterboxd username or hide your
              public page — and re-uploading a fresh export resets the count.
            </p>
          </Clause>

          <Clause title="Your account">
            <p>
              Signing in with Discord, Spotify, or Google shares your email
              address, display name, and avatar with us (via Supabase Auth). We
              use them to show who's who on blends and to sign you in — nothing
              else. If you add your Letterboxd username, we use it for your
              public page address.
            </p>
          </Clause>

          <Clause title="What's public">
            <p>
              Your taste page (twoshot.xyz/your-username) and any blend you
              create are viewable by anyone with the link — that's the point of
              them. They show your taste stats, not your email or account
              details. You can hide your public page any time with the checkbox
              on your dashboard.
            </p>
          </Clause>

          <Clause title="AI features">
            <p>
              Taste summaries, badges, and "watch tonight" picks are generated
              by large language models (via OpenRouter). We send film titles,
              years, and ratings — never your name, email, or anything that
              identifies you. Generated text is marked "AI-generated" in the
              app.
            </p>
          </Clause>

          <Clause title="Services we rely on">
            <p>
              Supabase stores the data described above. Cloudflare serves the
              site (standard server logs). TMDB provides posters and
              where-to-watch info — we send it film titles to look up, nothing
              about you. That's the whole list: no ad networks, no third-party
              trackers.
            </p>
          </Clause>

          <Clause title="Analytics">
            <p>
              We log basic product events (page path, event name, browser type,
              a random session ID stored in your browser) into our own database
              to find bugs and see which features get used. This data stays with
              us and is never joined with anything outside twoshot.
            </p>
          </Clause>

          <Clause title="Deleting your data">
            <p>
              Email{" "}
              <a
                href={`mailto:${CONTACT}`}
                className="text-red-600 underline decoration-red-600/40 underline-offset-2"
              >
                {CONTACT}
              </a>{" "}
              from your account address and we'll delete your account, your
              library data, and any blends you're part of — usually within a few
              days. Re-uploading a fresh export replaces your old snapshot
              automatically.
            </p>
          </Clause>

          <Clause title="Changes">
            <p>
              If this policy changes in a way that matters, we'll update the
              effective date above and note it on the site. Questions? Same
              email.
            </p>
          </Clause>
        </Field>

        <Tear />

        <Field className="flex items-center justify-between py-5">
          <span className="font-mono text-[10px] uppercase tracking-stamp text-ink-400">
            twoshot · effective {EFFECTIVE}
          </span>
          <Barcode className="w-24" />
        </Field>
      </Sheet>
    </Page>
  );
}
